// import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge"; // Custom Code
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
// import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand"; // Custom Code
// import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription"; // Custom Code
// import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage"; // Custom Code
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
// import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers"; // Custom Code
// import ProductPrice from "@insite/content-library/Components/ProductPrice"; // Custom Code
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
// import Badge from "@insite/mobius/Badge"; // Custom Code
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Custom Code - Start */
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import AddToCartOptionsAndButton, {
    AddToCartOptionsAndButtonStyles,
} from "@store/Library/Components/Toolkit/AddToCartOptionsAndButton";
import Badge from "@store/MobiusOverrides/Badge/Badge";
import { ProductAddToCartButtonStyles } from "@store/Overrides/Components/ProductAddToCartButton";
import ProductAttributes, { ProductAttributesStyles } from "@store/Overrides/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@store/Overrides/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@store/Overrides/Components/ProductBrand";
// import ProductDescription, { ProductDescriptionStyles } from "@store/Overrides/Components/ProductDescription"; // Store Custom Code
import useOverlayBadge from "@store/Framework/Common/Hooks/useOverlayBadge";
import { getSettingsCollection } from "@store/Framework/Store/Context/ContextSelectors";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import { productCardProps } from "@store/Themes/common/props/productCard";
/* Custom Code - End */
/* Store Custom Code - Start */
import PromoTag from "@store/Library/Components/PromoTag";
import ProductDescription, { ProductDescriptionStyles } from "@store/Overrides/Components/ProductDescription";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    // Custom Code
    showInventoryAvailability: getSettingsCollection(state).productSettings.showInventoryAvailability,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // Custom Code
    showAttributes: boolean;
    showAvailability: boolean;
    // Store Custom Code
    isSubstitute?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ProductAddToCartButtonStyles; // Custom Code
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // Custom Code
    attributesWrapper?: InjectableCss;
    attributes?: ProductAttributesStyles;
    availability?: ProductAvailabilityStyles;
    productActionsWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    addToCartOptionsAndButton?: AddToCartOptionsAndButtonStyles;
    productAddToListWrapper?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productImageStyles: productCardProps.image,
    productBrandStyles: productCardProps.brand,
    productDescriptionStyles: {
        ...productCardProps.description,
        wrapper: {
            ...productCardProps.description?.wrapper,
            forwardAs: "h3",
        },
    },
    attributesWrapper: productCardProps.spacingWrapper,
    productPartNumbersStyles: productCardProps.partNumbers,
    attributes: productCardProps.attributes,
    availability: productCardProps.availability,
    productActionsWrapper: productCardProps.actionsWrapper,
    productPriceWrapper: productCardProps.spacingWrapper,
    productPrice: productCardProps.price,
    productQuantityBreakPricingStyles: productCardProps.quantityBreakPricing,
    addToCartOptionsAndButton: productCardProps.addToCartOptionsAndButton,
    productAddToListWrapper: productCardProps.addToListWrapper,
    productAddToListLinkStyles: productCardProps.addToListLink,
    badgeAreaWrapper: productCardProps.badgeAreaWrapper,
    badgeTextWrapper: productCardProps.badgeTextWrapper,
    badgeText: productCardProps.badgeText,
    badgeImageWrapper: productCardProps.badgeImageWrapper,
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    // Custom Code
    showAttributes,
    showAvailability,
    showInventoryAvailability,
    // Store Custom Code
    isSubstitute,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const canAddToList = showAddToList && product.canAddToWishlist; // Custom Code

    /* Store Custom Code - Start */
    const parentUrl = productContext.product.properties.variantParentPath;
    const canonicalUrl = productContext.product.canonicalUrl;
    const cardUrl = isSubstitute ? parentUrl : canonicalUrl;
    /* Store Custom Code - End */

    return (
        <>
            <ProductContext.Provider value={productContext}>
                {/* Store Custom Code - Start */}
                {showImage && !showOverlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCarouselBadge">
                        <PromoTag product={productContext.product} productInfo={productContext.productInfo} />
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                            size="medium" // Custom Code
                            href={cardUrl} // Store Custom Code
                        />
                    </StyledWrapper>
                )}
                {/* Store Custom Code - End */}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                        {...styles.badgeAreaWrapper}
                        ref={badgeAreaWrapperRef}
                        data-test-selector="productCarouselBadge"
                    >
                        {/* Store Custom Code - Start */}
                        <PromoTag product={productContext.product} productInfo={productContext.productInfo} />
                        {/* Store Custom Code - End */}
                        <ProductImage
                            extendedStyles={styles.productImageStyles}
                            product={productContext}
                            size="medium" // Custom Code
                            href={cardUrl} // Store Custom Code
                        />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round"} // Custom Code
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {showBrand && product.brand && (
                    /* Custom Code - Start */
                    <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    /* Custom Code - End */
                )}
                {showTitle && (
                    <ProductDescription
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productDescriptionStyles}
                        href={cardUrl} // Store Custom Code
                    />
                )}
                {/* Custom Code - Start */}
                {showPartNumbers || showAttributes ? (
                    <StyledWrapper {...styles.attributesWrapper}>
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                showCustomerName={false}
                                showManufacturerItem={false}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        )}
                        {showAttributes ? (
                            <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                        ) : null}
                    </StyledWrapper>
                ) : null}
                {showInventoryAvailability && showAvailability ? (
                    <ProductContextAvailability extendedStyles={styles.availability} />
                ) : null}
                {/* Custom Code - End */}
                {showTextBadges && textBadges && (
                    <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                )}
                {showImageBadges && imageBadges && (
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                )}
                {showPrice || showAddToCart || showAddToList /* Custom Code */ ? (
                    <StyledWrapper {...styles.productActionsWrapper /* Custom Code */}>
                        {showPrice && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                {/* Removed - label */}
                                <ProductPrice
                                    showLabel={false}
                                    product={productContext}
                                    extendedStyles={styles.productPrice}
                                />
                                {/* Removed - unitOfMeasures */}
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.productQuantityBreakPricingStyles}
                                />
                            </StyledWrapper>
                        )}
                        {showAddToCart && (
                            /* Custom Code - Start */
                            <AddToCartOptionsAndButton
                                extendedStyles={{
                                    ...styles.addToCartOptionsAndButton,
                                    addToCartButton: {
                                        "data-test-selector": `addToCartBtn`,
                                        onAddToCartClick: () => trackProductRecommendation(false),
                                    },
                                }}
                            />
                            /* Custom Code - End */
                        )}
                        {canAddToList /* Custom Code */ && (
                            <StyledWrapper {...styles.productAddToListWrapper /* Custom Code */}>
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                ) : null}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
